import { useDragDropFiles } from 'components/upload/useDragDropFiles';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useFileUpload } from 'pages/Settings/FileLibrary/hooks/useFileUpload';
import { ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { FileTypePatterns, normalizeFile } from 'utils/file';
import { sentryCaptureException } from 'utils/sentry';

const Container = styled.div`
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

export type FileDropProps = Readonly<{
    children: ReactNode;
    corporationId?: string;
    locationId?: string;
    parentFolderId?: string;
    groupIds?: string[];
    folderName?: string;
    onUpload: () => void;
    disabled?: boolean;
}>;

export function FileDrop({
    children,
    corporationId,
    locationId,
    parentFolderId,
    groupIds,
    folderName = 'root',
    onUpload,
    disabled = false,
}: FileDropProps) {
    const { showToast } = useNotificationContext();
    const {
        mutation: { mutate: uploadFile },
    } = useFileUpload(
        {
            locationId,
            corporationId,
            parentFolderId,
            groupIds,
            allowedFileTypes: [FileTypePatterns.image, FileTypePatterns.video],
        },
        { onSuccess: onUpload },
    );

    const onFiles = useCallback(
        (files: File[]) => {
            for (const file of files) {
                normalizeFile(file)
                    .then(uploadFile)
                    .catch((err) => {
                        sentryCaptureException({
                            error: err instanceof Error ? err : new Error(String(err)),
                            tags: { component: 'FileDrop' },
                            context: {
                                file: {
                                    fileName: file.name,
                                    fileSize: file.size,
                                    fileType: file.type,
                                },
                            },
                        });
                        showToast({
                            type: 'error',
                            content: `Failed to process file: ${file.name}. The file may be corrupted.`,
                        });
                    });
            }
        },
        [uploadFile, showToast],
    );

    const { dragProps, overlay } = useDragDropFiles({ onFiles, folderName, disabled });

    return (
        <Container {...dragProps}>
            {children}
            {overlay}
        </Container>
    );
}
